import {
  collection,
  deleteDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  doc,
  limit,
  where,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { db, functions, httpsCallable, storage } from '../firebase';

const SEND_PREINVOICES_FN = httpsCallable(functions, 'sendPreinvoices');

export const PREINVOICE_STATUSES = ['Sent', 'pending payment', 'Payed', 'sendError'];

export const SELECTABLE_PAYMENT_STATUSES = ['Sent', 'pending payment', 'Payed'];

export const PREINVOICE_EMAIL_FROM_NAME = 'TenisoUžsiėmimai.lt';

// Placeholders are replaced per-recipient on the server side.
// Keep in sync with PREINVOICE_DEFAULT_MESSAGE_TEMPLATE in functions/index.js.
export const PREINVOICE_DEFAULT_MESSAGE_TEMPLATE = `Laba diena, {{vardas}},

Siunčiame išankstinės sąskaitą faktūrą už planuojamas suteikti paslaugas. PDF failas laiško prisegtuke. Taip pat sąskaitą galite peržiūrėti paspaudę nuorodos mygtuką apačioje.

Suma: {{suma}}
Sąskaitos numeris: {{saskaitosNumeris}}

{{pdfUrl}}

Linkime malonaus laiko aikštelėje!
${PREINVOICE_EMAIL_FROM_NAME}`;

export const PREINVOICE_TEMPLATE_PLACEHOLDERS = [
  { key: '{{vardas}}', description: 'kliento vardas' },
  { key: '{{pdfUrl}}', description: 'PDF nuoroda (HTML laiške rodoma kaip mygtukas „Atsisiųsti sąskaitą (PDF)")' },
  { key: '{{suma}}', description: 'suma (pvz. „40.00 Eur“)' },
  { key: '{{saskaitosNumeris}}', description: 'sąskaitos numeris' },
  { key: '{{data}}', description: 'metai-mėnuo (pvz. „2026-05“)' }
];

/** Client-side preview of a template for one recipient. Mirrors server substitution. */
export const previewPreinvoiceMessage = (template, vars) => {
  return String(template || '').replace(/\{\{(\w+)\}\}/g, (m, key) => {
    if (vars && Object.prototype.hasOwnProperty.call(vars, key) && vars[key] != null) {
      return String(vars[key]);
    }
    return m;
  });
};

export const getPreinvoiceRecipients = async () => {
  const snapshot = await getDocs(collection(db, 'userProfiles'));
  const rows = snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((u) => !u.blocked && u.email && String(u.email).includes('@'))
    .map((u) => ({
      id: u.id,
      userId: u.userId || '',
      fullName: u.fullName || '',
      email: String(u.email || '').trim().toLowerCase(),
      phoneNumber: String(u.phoneNumber || u.phone || '').trim(),
      createdAt: u.createdAt || null,
      updatedAt: u.updatedAt || null
    }));

  rows.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() ?? a.updatedAt?.toMillis?.() ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? b.updatedAt?.toMillis?.() ?? 0;
    return tb - ta; // newest first
  });
  return rows;
};

export const sendPreinvoices = async ({ amountEur, recipients, isTest, messageTemplate, paymentStatus }) => {
  const response = await SEND_PREINVOICES_FN({
    amountEur,
    recipients,
    isTest: isTest === true,
    messageTemplate: typeof messageTemplate === 'string' ? messageTemplate : '',
    paymentStatus: SELECTABLE_PAYMENT_STATUSES.includes(paymentStatus) ? paymentStatus : 'pending payment'
  });
  return response?.data || { ok: false, results: [] };
};

export const subscribeToRecentPreinvoices = (year, callback) => {
  const yearRef = collection(db, 'preinvoices', String(year), 'items');
  const q = query(yearRef, orderBy('createdAt', 'desc'), limit(100));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(list);
  }, (error) => {
    console.error('preinvoices snapshot error:', error);
    callback([]);
  });
};

/** Client view: preinvoices assigned to this user (by email or auth uid). */
export const subscribeToClientPreinvoices = (year, clientEmail, clientUid, callback) => {
  const normalizedEmail = String(clientEmail || '').trim().toLowerCase();
  if (!normalizedEmail && !clientUid) {
    callback([]);
    return () => {};
  }
  const yearRef = collection(db, 'preinvoices', String(year), 'items');
  const q = normalizedEmail
    ? query(yearRef, where('clientEmail', '==', normalizedEmail), orderBy('createdAt', 'desc'), limit(100))
    : query(yearRef, where('clientId', '==', clientUid), orderBy('createdAt', 'desc'), limit(100));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, (error) => {
    console.error('client preinvoices snapshot error:', error);
    callback([]);
  });
};

export const updatePreinvoiceStatus = async (year, docId, status) => {
  if (!PREINVOICE_STATUSES.includes(status)) {
    throw new Error('Neteisingas statusas');
  }
  const ref = doc(db, 'preinvoices', String(year), 'items', docId);
  await updateDoc(ref, { status });
};

const parsePreinvoiceSequence = (item) => {
  const direct = Number(item?.sequence);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const match = String(item?.invoiceNumber || '').match(/^PR-(\d{4})-(\d+)$/i);
  if (!match) return null;
  const seq = Number(match[2]);
  return Number.isFinite(seq) && seq > 0 ? seq : null;
};

/** Return deleted PR number to the pool so the next preinvoice can reuse it. */
export const releasePreinvoiceSequence = async (year, sequence) => {
  const seq = Number(sequence);
  if (!Number.isFinite(seq) || seq <= 0) return;

  const counterRef = doc(db, 'counters', `preinvoice_${year}`);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const data = snap.exists() ? (snap.data() || {}) : {};
    let lastNumber = Number(data.lastNumber || 0);
    let freeSequences = Array.isArray(data.freeSequences)
      ? data.freeSequences.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : [];

    if (seq > lastNumber) {
      return;
    }
    freeSequences.push(seq);
    freeSequences = [...new Set(freeSequences)].sort((a, b) => a - b);

    while (lastNumber > 0 && freeSequences.includes(lastNumber)) {
      freeSequences = freeSequences.filter((n) => n !== lastNumber);
      lastNumber -= 1;
    }

    tx.set(counterRef, {
      lastNumber,
      freeSequences,
      updatedAt: Timestamp.now()
    }, { merge: true });
  });
};

/**
 * Delete one preinvoice entry, related queued mails and stored PDF.
 * - preinvoices/{year}/items/{docId}
 * - mail docs where reservationId == docId
 * - Storage file at item.pdfStoragePath (if present)
 */
export const deletePreinvoiceEntry = async (year, item) => {
  const docId = item?.id;
  if (!docId) {
    throw new Error('Trūksta sąskaitos įrašo ID');
  }

  const tasks = [];

  if (item?.pdfStoragePath) {
    tasks.push(
      deleteObject(ref(storage, item.pdfStoragePath)).catch((error) => {
        // Storage delete may be forbidden for client-side admin users (403).
        // Do not block invoice/mail cleanup if PDF cannot be removed here.
        if (error?.code !== 'storage/object-not-found') {
          console.warn('Skipping PDF delete for preinvoice:', error?.code || error?.message || error);
        }
      })
    );
  }

  tasks.push(deleteDoc(doc(db, 'preinvoices', String(year), 'items', docId)));

  const mailQ = query(collection(db, 'mail'), where('reservationId', '==', docId));
  const mailSnap = await getDocs(mailQ);
  mailSnap.forEach((mailDoc) => {
    tasks.push(deleteDoc(mailDoc.ref));
  });

  await Promise.all(tasks);

  if (item?.isTest !== true) {
    const sequence = parsePreinvoiceSequence(item);
    if (sequence) {
      try {
        await releasePreinvoiceSequence(year, sequence);
      } catch (err) {
        console.warn('Failed to release preinvoice sequence:', err?.message || err);
      }
    }
  }
};
